/* Voicemail: the messages of the device's lines (listen, call back, keep,
 * delete) and the greetings (listen, record or upload, choose the active one).
 * Same page as the desktop app's VoicemailPage.qml. */
import { useEffect, useRef, useState } from "preact/hooks";
import { t } from "../i18n";
import { Icon } from "../icon";
import { GreetingError, MicRecorder, toGreeting } from "../greeting";
import * as S from "../state";
import { IconButton, Segmented } from "../widgets";

function mmss(ms: number | null | undefined): string {
  const s = Math.round((ms ?? 0) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const loc = document.documentElement.lang === "en" ? "en-CA" : "fr-CA";
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString(loc, { dateStyle: "short", timeStyle: "short" });
}

/** One audio element for the page: messages, greetings and the draft. */
const player = typeof Audio !== "undefined" ? new Audio() : null;

function usePlayer() {
  const [playing, setPlaying] = useState(""); // what is loaded ("" none)
  const [paused, setPaused] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  useEffect(() => {
    if (!player) return;
    const tick = () => {
      setPos(player.currentTime * 1000);
      setDur(Number.isFinite(player.duration) ? player.duration * 1000 : 0);
    };
    const end = () => {
      setPlaying("");
      setPaused(false);
    };
    player.addEventListener("timeupdate", tick);
    player.addEventListener("durationchange", tick);
    player.addEventListener("seeked", tick);
    player.addEventListener("ended", end);
    return () => {
      player.pause();
      player.removeEventListener("timeupdate", tick);
      player.removeEventListener("durationchange", tick);
      player.removeEventListener("seeked", tick);
      player.removeEventListener("ended", end);
    };
  }, []);
  /** the same one again: pause / resume where it was; another: from the start */
  const play = async (id: string, url: () => Promise<string | null>) => {
    if (!player) return;
    if (playing === id) {
      if (player.paused) {
        setPaused(false);
        await player.play().catch(() => setPaused(true));
      } else {
        player.pause();
        setPaused(true);
      }
      return;
    }
    const u = await url();
    if (!u) return;
    player.src = u;
    setPos(0);
    setDur(0);
    setPlaying(id);
    setPaused(false);
    await player.play().catch(() => setPlaying(""));
  };
  const seek = (ms: number) => {
    if (!player || !playing) return;
    const end = Number.isFinite(player.duration) ? player.duration : ms / 1000;
    player.currentTime = Math.max(0, Math.min(end, ms / 1000));
    setPos(player.currentTime * 1000);
  };
  /** playing now (not paused) */
  const on = (id: string) => playing === id && !paused;
  return { playing, paused, pos, dur, play, seek, on };
}

/** The open message's player: where it is, a slider to go anywhere, 10 s back and ahead. */
function Seeker({ p, total }: { p: ReturnType<typeof usePlayer>; total: number }) {
  const dur = p.dur || total;
  return (
    <div class="vm-progress">
      <button type="button" class="vm-skip" aria-label={t("vm.back10")} title={t("vm.back10")} onClick={() => p.seek(p.pos - 10_000)}>
        −10
      </button>
      <input
        type="range"
        class="vm-seek"
        min={0}
        max={Math.max(1, Math.round(dur))}
        step={100}
        value={Math.round(p.pos)}
        aria-label={t("vm.position")}
        aria-valuetext={`${mmss(p.pos)} / ${mmss(dur)}`}
        onInput={(e) => p.seek(Number((e.target as HTMLInputElement).value))}
        style={{ "--fill": `${dur ? Math.min(100, (p.pos / dur) * 100) : 0}%` }}
      />
      <button type="button" class="vm-skip" aria-label={t("vm.ahead10")} title={t("vm.ahead10")} onClick={() => p.seek(p.pos + 10_000)}>
        +10
      </button>
      <span class="sub vm-time">
        {mmss(p.pos)} / {mmss(dur)}
      </span>
    </div>
  );
}

function Messages({ p }: { p: ReturnType<typeof usePlayer> }) {
  const [confirm, setConfirm] = useState("");
  const list = S.vmMessages.value;
  if (!list.length) return <p class="muted empty">{t("vm.none")}</p>;
  return (
    <ul class="vm-list" data-testid="vm-messages">
      {list.map((m) => {
        const playing = p.playing === m.id;
        const on = p.on(m.id);
        return (
          <li key={m.id} class={`vm-row ${playing ? "playing" : ""}`} data-id={m.id} data-read={String(m.read)}>
            <div class="vm-main">
              <button type="button" class="vm-play" aria-label={on ? t("vm.pause") : t("vm.listen")} onClick={() => void p.play(m.id, () => S.vmAudio(m.id))}>
                <Icon name={on ? "pause" : "play"} size={18} />
              </button>
              <div class="grow">
                <div class="vm-from">
                  <strong class={m.read ? "" : "unread"}>{m.from.name || m.from.number || t("vm.unknown")}</strong>
                  {!m.read && <span class="chip">{t("vm.new")}</span>}
                  {m.saved && <Icon name="star" size={14} />}
                </div>
                <div class="sub">
                  {[m.from.name ? m.from.number : null, when(m.received_at), mmss(m.duration_ms)].filter(Boolean).join("  ·  ")}
                </div>
              </div>
              {confirm === m.id ? (
                <div class="vm-confirm">
                  <span>{t("vm.delete_q")}</span>
                  <button type="button" class="btn" onClick={() => setConfirm("")}>{t("vm.no")}</button>
                  <button type="button" class="btn danger" data-testid="vm-confirm" onClick={() => { setConfirm(""); void S.vmDelete(m.id); }}>
                    {t("vm.yes_delete")}
                  </button>
                </div>
              ) : (
                <div class="vm-actions">
                  {m.from.number && <IconButton icon="phone" label={t("vm.call_back")} onClick={() => void S.dial(m.from.number!)} />}
                  <IconButton icon="star" label={m.saved ? t("vm.unkeep") : t("vm.keep")} pressed={m.saved} onClick={() => void S.vmMark(m.id, { saved: !m.saved })} />
                  <IconButton icon="trash" label={t("vm.delete")} onClick={() => setConfirm(m.id)} />
                </div>
              )}
            </div>
            {playing && <Seeker p={p} total={m.duration_ms ?? 0} />}
          </li>
        );
      })}
    </ul>
  );
}

function Greetings({ p }: { p: ReturnType<typeof usePlayer> }) {
  const g = S.vmGreetings.value;
  const [slot, setSlot] = useState("1");
  const [draft, setDraft] = useState<{ wav: ArrayBuffer; ms: number; url: string } | null>(null);
  const [rec, setRec] = useState<MicRecorder | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [err, setErr] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const max = (g?.max_seconds ?? 120) * 1000;

  useEffect(() => {
    if (!rec) return;
    const t0 = Date.now();
    const i = setInterval(() => {
      setElapsed(Date.now() - t0);
      setLevel(rec.level());
      if (Date.now() - t0 >= max) void stop();
    }, 100);
    return () => clearInterval(i);
  }, [rec]);

  const take = async (data: ArrayBuffer) => {
    try {
      const d = await toGreeting(data);
      if (draft) URL.revokeObjectURL(draft.url);
      setDraft({ ...d, url: URL.createObjectURL(new Blob([d.wav], { type: "audio/wav" })) });
      setErr("");
    } catch (e) {
      setErr(e instanceof GreetingError ? e.code : "unsupported");
    }
  };
  const start = async () => {
    const r = new MicRecorder();
    try {
      await r.start(S.prefs.value.source || undefined);
      setDraft(null);
      setErr("");
      setRec(r);
    } catch {
      setErr("mic");
    }
  };
  const stop = async () => {
    if (!rec) return;
    const r = rec;
    setRec(null);
    await take(await r.stop());
  };
  const save = async () => {
    if (draft && (await S.vmSaveGreeting(slot, draft.wav))) {
      URL.revokeObjectURL(draft.url);
      setDraft(null);
    }
  };
  const errText: Record<string, string> = {
    silence: t("vm.err_silence"),
    unsupported: t("vm.err_unsupported"),
    too_long: t("vm.err_too_long"),
    mic: t("vm.err_mic"),
  };
  const slotName = (s: string) => (s === "name" ? t("vm.your_name") : t("vm.greeting_n", s));

  return (
    <div class="vm-greetings" data-testid="vm-greetings">
      <p class="muted">{t("vm.greetings_help")}</p>
      <label class="vm-radio">
        <input type="radio" name="vm-active" checked={!g?.active_slot} onChange={() => void S.vmSetActive(null)} data-testid="vm-default" />
        {t("vm.default_greeting")}
      </label>
      {g?.slots.map((x) => (
        <div class="vm-row vm-main" key={x.slot}>
          <input type="radio" name="vm-active" aria-label={slotName(String(x.slot))} checked={g.active_slot === x.slot} onChange={() => void S.vmSetActive(x.slot)} />
          <div class="grow">
            <strong>{slotName(String(x.slot))}</strong>
            <div class="sub">{[mmss(x.duration_ms), when(x.recorded_at)].filter(Boolean).join("  ·  ")}</div>
          </div>
          <IconButton icon={p.on(`g${x.slot}`) ? "pause" : "play"} label={t("vm.listen")} onClick={() => void p.play(`g${x.slot}`, () => S.vmGreetingAudio(String(x.slot)))} />
          <IconButton icon="trash" label={t("vm.delete")} onClick={() => void S.vmDeleteGreeting(String(x.slot))} />
        </div>
      ))}
      {g?.name && (
        <div class="vm-main">
          <span class="grow">{t("vm.recorded_name")}</span>
          <IconButton icon={p.on("gname") ? "pause" : "play"} label={t("vm.listen")} onClick={() => void p.play("gname", () => S.vmGreetingAudio("name"))} />
        </div>
      )}

      <section class="card vm-new">
        <h3>{t("vm.new_greeting")}</h3>
        <label>
          {t("vm.save_as")}{" "}
          <select value={slot} onChange={(e) => setSlot((e.target as HTMLSelectElement).value)}>
            {["1", "2", "3", "name"].map((s) => (
              <option value={s}>{slotName(s)}</option>
            ))}
          </select>
        </label>
        {!rec && !draft && (
          <div class="row">
            <button type="button" class="btn primary" data-testid="vm-record" onClick={() => void start()}>
              <Icon name="mic" size={18} /> {t("vm.record")}
            </button>
            <button type="button" class="btn" onClick={() => file.current?.click()}>
              <Icon name="upload" size={18} /> {t("vm.choose_file")}
            </button>
            <input ref={file} type="file" accept="audio/*" hidden data-testid="vm-file" onChange={async (e) => {
              const f = (e.target as HTMLInputElement).files?.[0];
              if (f) await take(await f.arrayBuffer());
              (e.target as HTMLInputElement).value = "";
            }} />
          </div>
        )}
        {rec && (
          <div class="row">
            <span class="rec-dot" />
            <span>{t("vm.recording", mmss(elapsed), mmss(max))}</span>
            <progress class="grow" max={1} value={Math.min(1, level * 3)} />
            <button type="button" class="btn primary" data-testid="vm-stop" onClick={() => void stop()}>
              <Icon name="stop" size={18} /> {t("vm.stop")}
            </button>
          </div>
        )}
        {draft && (
          <div class="row">
            <span class="grow">{t("vm.ready", mmss(draft.ms))}</span>
            <button type="button" class="btn" onClick={() => void p.play("draft", async () => draft.url)}>
              <Icon name={p.on("draft") ? "pause" : "play"} size={18} /> {p.on("draft") ? t("vm.pause") : t("vm.listen")}
            </button>
            <button type="button" class="btn" onClick={() => setDraft(null)}>{t("vm.discard")}</button>
            <button type="button" class="btn primary" data-testid="vm-save" onClick={() => void save()}>{t("vm.save")}</button>
          </div>
        )}
        {err && <p class="error">{errText[err] ?? err}</p>}
        <p class="muted small">{t("vm.limits")}</p>
      </section>
    </div>
  );
}

export function Voicemail() {
  const [tab, setTab] = useState<"messages" | "greetings">("messages");
  const p = usePlayer();
  useEffect(() => {
    void S.vmRefresh();
    void S.vmLoadGreetings();
  }, []);
  const unread = S.vmMessages.value.filter((m) => !m.read).length;
  return (
    <section class="page voicemail">
      <header class="page-head">
        <h1>{t("vm.title")}</h1>
        <button type="button" class="btn" onClick={() => void S.dial(S.voicemailNumber())}>
          <Icon name="phone" size={18} /> {t("vm.call_mailbox")}
        </button>
      </header>
      <Segmented
        label={t("vm.title")}
        value={tab}
        options={[
          ["messages", unread ? t("vm.messages_new", unread) : t("vm.messages")],
          ["greetings", t("vm.greetings")],
        ]}
        onChange={setTab}
      />
      {S.vmError.value && S.vmError.value !== "not_found" && <p class="error">{t("vm.err_generic", S.vmError.value)}</p>}
      {tab === "messages" ? <Messages p={p} /> : <Greetings p={p} />}
    </section>
  );
}
